"use client";
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { ModuleSettingsStore } from "../../../../utils/contentstack/moduleSettingsStore.js";
import { useStwContext } from "../../../../utils/stwInfo/hook.js";
import { getWarehouseOptionsDetails } from "../../../../utils/warehouses/findAWarehouse.lib.js";
import { TITLE_TEXT_KEY, BODY_TEXT_KEY, DRAWER_FIND_BTN_LABEL_KEY, WAREHOUSE_PRICING_MAY_VARY_TEXT_KEY, CLOSE_BTN_ARIA_LABEL_KEY, WAREHOUSE_FILTERS_FIELDSET_KEY, MAX_WAREHOUSES_PER_BATCH_PROPERTY_KEY } from "../../../../utils/warehouses/warehouseDrawer.constants.js";
import { memo, useMemo, useCallback } from "react";
import { AccordionSummary, AccordionDetails, Box } from "@mui/material";
import { DrawerV2, Text, Divider, Button, Spinner, Notification } from "@nxt-costco-com/forge-components";
import { ColorWhite, ColorGray300, ColorCostcoBlue, SpaceMd, SpaceXl, SpaceXs } from "@nxt-costco-com/forge-design-tokens";
import { Alert } from "@nxt-costco-com/forge-icons";
import { Analytics } from "../../../Analytics/index.js";
import { WarehouseTile } from "../WarehouseTile/index.js";
import { WarehouseTileSkeleton } from "../WarehouseTile/view.skeleton.js";
import { BATCH_SIZE_MAX, COMPONENT_NAME, SEARCH_FIELD_MAX_LENGTH } from "../constants.js";
import { useWarehouseSearch } from "../hooks/useWarehouseSearch.js";
import { useWarehouseStockStatus } from "../hooks/useWarehouseStockStatus.js";
import { WarehouseOptionCheckbox } from "../../WarehouseOptionCheckbox/index.js";
import { ProductImage } from "../../../Product/ProductImage/index.js";
import { shouldShowSetWarehouseButton, getNotificationMessage } from "./utils/warehouseDrawer.lib.js";
import { StyledHeader, StyledProductHeader, ProductImageSx, ProductDecriptionTextSx, StyledForm, StyledAutoComplete, StyledLocationSuggestionListItem, StyledTextField, StyledAccordion, AccordianSummarySx, StyledChevronDown, StyledHiddenLegend, StyledSpinnerWrapper, StyledNotifcationWrapper } from "./styles.js";
const WrappedWarehouseTile = memo(
  ({
    moduleSettings,
    preferredWarehouse,
    onClose,
    onUpdatePreferredWarehouse,
    warehouse,
    locale,
    drawerType,
    pageName,
    productFlags,
    featureFlags
  }) => {
    const { updateStwInfo } = useStwContext();
    const isCMW = drawerType === "CMW";
    const onSetWarehouse = () => {
      var _a, _b, _c, _d, _e;
      const city = (_b = (_a = warehouse == null ? void 0 : warehouse.name) == null ? void 0 : _a[0]) == null ? void 0 : _b.value;
      const countryCode = (_c = warehouse == null ? void 0 : warehouse.address) == null ? void 0 : _c.countryName;
      const postalCode = (_d = warehouse == null ? void 0 : warehouse.address) == null ? void 0 : _d.postalCode;
      const territory = (_e = warehouse == null ? void 0 : warehouse.address) == null ? void 0 : _e.territory;
      const warehouseId = warehouse == null ? void 0 : warehouse.warehouseId;
      if (isCMW) {
        updateStwInfo({
          stwMyWarehouse: warehouseId,
          stwPostalCode: postalCode.split("-")[0]
        });
      }
      const success = onUpdatePreferredWarehouse({
        city,
        countryCode,
        postalCode,
        territory,
        warehouseId,
        warehouseDrawer: drawerType,
        ...isCMW && {
          itemType: (productFlags == null ? void 0 : productFlags.isShipToWarehouse) ? "STW" : "BOPIW",
          pageName: pageName ?? "PDP",
          orderItemId: ""
        }
      });
      if (success) {
        onClose();
      }
      return success;
    };
    const isPreferredWarehouse = warehouse.warehouseId === preferredWarehouse;
    const showSetWarehouseButton = shouldShowSetWarehouseButton(
      locale,
      warehouse,
      drawerType,
      isPreferredWarehouse
    );
    return /* @__PURE__ */ jsx(
      WarehouseTile,
      {
        isPreferredWarehouse,
        locale,
        moduleSettings,
        onSetWarehouse,
        warehouseData: warehouse,
        productFlags,
        featureFlags,
        showSetWarehouseButton
      },
      warehouse.warehouseId
    );
  }
);
WrappedWarehouseTile.displayName = "WrappedWarehouseTile";
const DrawerHeader = ({
  isFMW,
  isCMW,
  productImageData,
  handlers,
  labels,
  actions,
  bodyText,
  formik,
  drawerFindBtnLabel,
  warehouseFiltersLegend,
  options,
  drawerOptionsDetails,
  isFiltersOpen,
  state,
  handleFilterCheckbox,
  wioEnabledPricingText,
  announcement,
  analyticsIdentifier
}) => {
  return /* @__PURE__ */ jsxs(StyledHeader, { children: [
    isFMW && /* @__PURE__ */ jsx(Text, { uniqueId: "warehousedrawer-header-bodytext", variant: "t6", children: bodyText }),
    isCMW && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(StyledProductHeader, { children: [
        /* @__PURE__ */ jsx(
          ProductImage,
          {
            srcFallback: (productImageData == null ? void 0 : productImageData.srcFallback) ?? "",
            titleFallback: (productImageData == null ? void 0 : productImageData.titleFallback) ?? "",
            ariaHidden: true,
            src: (productImageData == null ? void 0 : productImageData.src) ?? "",
            sx: ProductImageSx,
            title: (productImageData == null ? void 0 : productImageData.title) ?? "",
            uniqueId: "warehousedrawer-header-productimage"
          }
        ),
        /* @__PURE__ */ jsx(
          Text,
          {
            sx: ProductDecriptionTextSx,
            uniqueId: "warehousedrawer-header-productdescription",
            variant: "t6",
            component: "h3",
            children: productImageData == null ? void 0 : productImageData.description
          }
        )
      ] }),
      /* @__PURE__ */ jsx(Divider, {})
    ] }),
    /* @__PURE__ */ jsxs(StyledForm, { onSubmit: handlers.handleSubmit, children: [
      /* @__PURE__ */ jsx(
        StyledAutoComplete,
        {
          slotProps: {
            listbox: {
              "aria-label": labels.aria.listBoxAriaLabel
            }
          },
          onClose: () => actions.setIsAutocompleteDropdownOpen(false),
          onKeyDown: (event) => {
            if (event.key === "Enter") {
              actions.selectFirstOption();
              actions.setOptions([]);
              handlers.handleSubmit(event);
            }
          },
          onOpen: () => actions.setIsAutocompleteDropdownOpen(true),
          renderInput: (params) => /* @__PURE__ */ jsx(
            StyledTextField,
            {
              "data-testid": "warehousedrawer-textfield",
              ...params,
              slotProps: {
                input: {
                  ...params.InputProps,
                  endAdornment: /* @__PURE__ */ jsx(
                    Button,
                    {
                      onClick: (event) => {
                        actions.selectFirstOption();
                        handlers.handleSubmit(event);
                      },
                      sx: {
                        backgroundColor: ColorCostcoBlue
                      },
                      uniqueId: "warehousedrawer-submit",
                      variant: "primary",
                      children: drawerFindBtnLabel
                    }
                  )
                },
                htmlInput: {
                  ...params.inputProps,
                  maxLength: SEARCH_FIELD_MAX_LENGTH,
                  name: labels.searchFieldLabel
                }
              },
              label: labels.searchFieldLabel,
              size: "small",
              variant: "outlined"
            }
          ),
          renderOption: ({ key, ...props }, option) => {
            const optionTyped = option;
            return /* @__PURE__ */ jsxs(
              StyledLocationSuggestionListItem,
              {
                ...props,
                children: [
                  optionTyped.locality,
                  /* @__PURE__ */ jsx("br", {}),
                  optionTyped.address
                ]
              },
              optionTyped.address
            );
          },
          freeSolo: true,
          getOptionLabel: actions.getOptionLabel,
          inputValue: formik.values.searchText,
          onInputChange: handlers.onInputChange,
          options
        }
      ),
      isFMW && /* @__PURE__ */ jsxs(
        StyledAccordion,
        {
          "data-testid": "warehousedrawer-warehousefilter",
          disableGutters: true,
          slotProps: { transition: { timeout: 175 } },
          expanded: isFiltersOpen,
          onChange: () => {
            actions.setIsFiltersOpen(!isFiltersOpen);
          },
          children: [
            /* @__PURE__ */ jsx(
              Analytics,
              {
                analyticData: {
                  component: {
                    trackedData: [
                      isFiltersOpen ? labels.filters.hideWarehouseFiltersBtnText : labels.filters.showWarehouseFiltersBtnText
                    ]
                  }
                },
                children: /* @__PURE__ */ jsx(
                  AccordionSummary,
                  {
                    "aria-label": isFiltersOpen ? labels.filters.hideWarehouseFiltersBtnText : labels.filters.showWarehouseFiltersBtnText,
                    component: "button",
                    "data-testid": "warehousedrawer-warehousefilter-icon-chevron-down",
                    expandIcon: /* @__PURE__ */ jsx(StyledChevronDown, {}),
                    sx: AccordianSummarySx,
                    children: isFiltersOpen ? /* @__PURE__ */ jsx(
                      Text,
                      {
                        variant: "t6",
                        uniqueId: "warehousedrawer-warehousefilter-hidden",
                        children: labels.filters.hideWarehouseFiltersBtnText
                      }
                    ) : /* @__PURE__ */ jsx(
                      Text,
                      {
                        variant: "t6",
                        uniqueId: "warehousedrawer-warehousefilter-show",
                        children: labels.filters.showWarehouseFiltersBtnText
                      }
                    )
                  }
                )
              }
            ),
            /* @__PURE__ */ jsx(
              AccordionDetails,
              {
                "data-testid": "warehousedrawer-warehousefilter-details",
                sx: {
                  padding: 0,
                  paddingTop: SpaceMd
                },
                children: /* @__PURE__ */ jsxs(
                  Box,
                  {
                    sx: {
                      border: "none",
                      display: "flex",
                      flexDirection: "column",
                      gap: SpaceMd,
                      margin: 0,
                      padding: 0
                    },
                    component: "fieldset",
                    children: [
                      /* @__PURE__ */ jsx(StyledHiddenLegend, { children: warehouseFiltersLegend }),
                      drawerOptionsDetails.map((option) => {
                        return /* @__PURE__ */ jsx(
                          WarehouseOptionCheckbox,
                          {
                            isChecked: formik.values.filters[option.id],
                            onChange: (e) => {
                              formik.setFieldValue(
                                "filters",
                                {
                                  ...formik.values.filters,
                                  [option.id]: e.target.checked
                                }
                              );
                            },
                            option,
                            uniqueId: `warehousedrawer-${option.label}`
                          },
                          option.id
                        );
                      })
                    ]
                  }
                )
              }
            )
          ]
        }
      ),
      isCMW && /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs(Box, { children: [
        drawerOptionsDetails.map((option) => {
          return /* @__PURE__ */ jsx(
            Analytics,
            {
              analyticData: {
                component: {
                  identifier: analyticsIdentifier || "My Warehouse",
                  trackedData: [
                    "Check Nearby Warehouses Modal",
                    !state.checked ? "Check In-Stock Nearby" : "Uncheck In-Stock Nearby"
                  ],
                  type: "ui"
                },
                trackType: "navigation"
              },
              children: /* @__PURE__ */ jsx(
                WarehouseOptionCheckbox,
                {
                  isChecked: state.checked,
                  onChange: handleFilterCheckbox(),
                  option,
                  uniqueId: `warehousedrawer-${option.label}`
                },
                option.id
              )
            }
          );
        }),
        /* @__PURE__ */ jsx(
          Text,
          {
            sx: {
              alignItems: "center",
              alignSelf: "stretch",
              display: "flex",
              paddingLeft: SpaceXl
            },
            color: "light",
            uniqueId: "warehousedrawer-wioenabledtext",
            variant: "t7",
            children: wioEnabledPricingText
          }
        )
      ] }) }),
      /* @__PURE__ */ jsx(
        "div",
        {
          style: {
            left: "-9999px",
            position: "absolute"
          },
          "aria-live": "polite",
          role: "status",
          children: announcement
        }
      )
    ] })
  ] });
};
const DrawerBody = ({
  onUpdatePreferredWarehouse,
  onClose,
  isLoading,
  warehouses,
  locale,
  moduleSettings,
  preferredWarehouse,
  isFMW,
  isCMW,
  rootRef,
  sentinelRef,
  displayedWarehouses,
  drawerType,
  showSentinel,
  notificationAlert,
  pageName,
  productFlags,
  featureFlags
}) => {
  return /* @__PURE__ */ jsx(
    Analytics,
    {
      analyticData: {
        component: {
          identifier: `${isFMW ? "find a warehouse" : "check nearby warehouses"} flyout`,
          type: "ui"
        },
        trackType: "navigation"
      },
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          sx: {
            height: "100%",
            ...isLoading ? { backgroundColor: ColorWhite, opacity: 0.6 } : {}
          },
          children: [
            isLoading && /* @__PURE__ */ jsx(StyledSpinnerWrapper, { "data-testid": "warehousedrawer-spinner", children: /* @__PURE__ */ jsx(
              Spinner,
              {
                size: "medium",
                hasBackdrop: false,
                label: "loading-warehouses"
              }
            ) }),
            !isLoading && /* @__PURE__ */ jsxs(
              Box,
              {
                sx: {
                  display: "flex",
                  flex: "1 1 auto",
                  flexDirection: "column",
                  height: "100%",
                  gap: warehouses && (warehouses == null ? void 0 : warehouses.length) <= 1 ? "initial" : SpaceXs,
                  ...isCMW ? { overflowY: "auto" } : {}
                },
                ref: rootRef,
                children: [
                  displayedWarehouses && displayedWarehouses.length > 0 && displayedWarehouses.map((warehouse) => {
                    return /* @__PURE__ */ jsx(
                      WrappedWarehouseTile,
                      {
                        onUpdatePreferredWarehouse,
                        locale,
                        moduleSettings,
                        onClose,
                        preferredWarehouse,
                        warehouse,
                        drawerType,
                        pageName,
                        productFlags,
                        featureFlags
                      },
                      warehouse.warehouseId
                    );
                  }),
                  isCMW && showSentinel && /* @__PURE__ */ jsx(Box, { ref: sentinelRef, sx: { marginTop: SpaceXs }, children: /* @__PURE__ */ jsx(WarehouseTileSkeleton, {}) }),
                  notificationAlert && /* @__PURE__ */ jsx(StyledNotifcationWrapper, { children: /* @__PURE__ */ jsx(
                    Notification,
                    {
                      icon: /* @__PURE__ */ jsx(Alert, {}),
                      message: notificationAlert.message,
                      severity: notificationAlert.severity,
                      uniqueId: `warehousedrawer-${notificationAlert.uniqueId}`
                    }
                  ) })
                ]
              }
            )
          ]
        }
      )
    }
  );
};
const WarehouseDrawerUI = ({
  announcement,
  defaultFiltersVisible,
  getAutosuggest,
  isLoading,
  isOpen,
  moduleSettings,
  onClose,
  onSearch,
  onUpdatePreferredWarehouse,
  preferredWarehouse,
  warehouses,
  locale,
  isInline,
  drawerType,
  batchServiceConfig,
  childItemNumber,
  productImageData,
  stwErrorMessage,
  featureConfigs,
  enabledWarehouseListConfigs,
  pageName,
  productFlags,
  analyticsIdentifier
}) => {
  const isCMW = drawerType === "CMW";
  const isFMW = drawerType === "FMW";
  const msStore = useMemo(
    () => new ModuleSettingsStore({
      composable_window: moduleSettings.composable_window,
      key_value: moduleSettings.key_value,
      properties: moduleSettings.properties
    }),
    [moduleSettings]
  );
  const titleText = msStore.getKeyValueItem(TITLE_TEXT_KEY);
  const bodyText = msStore.getKeyValueItem(BODY_TEXT_KEY);
  const drawerFindBtnLabel = msStore.getKeyValueItem(
    DRAWER_FIND_BTN_LABEL_KEY
  );
  const wioEnabledPricingText = msStore.getKeyValueItem(
    WAREHOUSE_PRICING_MAY_VARY_TEXT_KEY
  );
  const closeButtonAriaLabel = msStore.getKeyValueItem(
    CLOSE_BTN_ARIA_LABEL_KEY
  );
  const warehouseFiltersLegend = msStore.getKeyValueItem(
    WAREHOUSE_FILTERS_FIELDSET_KEY
  );
  const batchSize = msStore.getPropertiesItem(
    MAX_WAREHOUSES_PER_BATCH_PROPERTY_KEY
  ) ?? BATCH_SIZE_MAX;
  const drawerOptionsDetails = useMemo(
    () => getWarehouseOptionsDetails(moduleSettings, false),
    [moduleSettings]
  );
  const { formik, options, labels, isFiltersOpen, actions, handlers } = useWarehouseSearch({
    moduleSettings,
    onSearch,
    defaultFiltersVisible,
    getAutosuggest,
    drawerType
  });
  const {
    state,
    resetCheckboxState,
    showSentinel,
    sentinelRef,
    rootRef,
    allWHList,
    handleFilterCheckbox
  } = useWarehouseStockStatus({
    warehouses,
    preferredWarehouse,
    locale,
    childItemNumber,
    batchSize,
    configs: {
      batchServiceConfig,
      featureConfigs,
      enabledWarehouseListConfigs
    },
    productFlags,
    pageName,
    isLoading
  });
  const handleCloseDrawer = useCallback(() => {
    onClose();
    formik.setFieldValue("searchText", "");
    actions.setOptions([]);
    resetCheckboxState();
  }, [onClose, resetCheckboxState]);
  const notificationAlert = useMemo(
    () => getNotificationMessage({
      preferredWarehouse,
      warehouses,
      allWHList,
      displayedWarehouses: state.warehouseList,
      stwErrorMessage: Boolean(stwErrorMessage),
      isLoading,
      isBatchLoading: state.isBatchLoading,
      isProductUnavailable: state.isProductUnavailable,
      checked: state.checked,
      msStore,
      drawerType
    }),
    [
      preferredWarehouse,
      warehouses,
      allWHList,
      state.warehouseList,
      stwErrorMessage,
      isLoading,
      state.isBatchLoading,
      state.isProductUnavailable,
      state.checked,
      msStore,
      drawerType
    ]
  );
  const displayedWarehouses = useMemo(
    () => isCMW ? state.warehouseList : warehouses,
    [isCMW, state.warehouseList, warehouses]
  );
  if (!moduleSettings || isCMW && (!batchServiceConfig || !featureConfigs || !enabledWarehouseListConfigs))
    return null;
  if (isInline) {
    return /* @__PURE__ */ jsxs("div", { style: { textAlign: "start" }, children: [
      /* @__PURE__ */ jsx(
        DrawerHeader,
        {
          handlers,
          actions,
          warehouseFiltersLegend,
          drawerOptionsDetails,
          announcement,
          formik,
          isFMW,
          isCMW,
          productImageData,
          labels,
          bodyText,
          altBodyText: msStore.key_value.altbody,
          drawerFindBtnLabel,
          options,
          isFiltersOpen,
          state,
          handleFilterCheckbox,
          wioEnabledPricingText,
          analyticsIdentifier
        }
      ),
      /* @__PURE__ */ jsx(
        DrawerBody,
        {
          onUpdatePreferredWarehouse,
          onClose: handleCloseDrawer,
          isLoading,
          warehouses,
          locale,
          moduleSettings,
          preferredWarehouse,
          isFMW,
          isCMW,
          rootRef,
          sentinelRef,
          displayedWarehouses,
          drawerType,
          showSentinel,
          notificationAlert,
          pageName,
          productFlags,
          featureFlags: featureConfigs == null ? void 0 : featureConfigs.custom
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsx(
    DrawerV2,
    {
      PaperProps: {
        "aria-label": labels.aria.panelAriaLabel,
        "data-testid": COMPONENT_NAME
      },
      bodySx: {
        backgroundColor: !(warehouses == null ? void 0 : warehouses.length) || warehouses.length === 1 && warehouses[0].warehouseId === preferredWarehouse ? ColorWhite : ColorGray300,
        margin: 0,
        padding: 0,
        ...isCMW ? { overflow: "hidden" } : {}
      },
      headerProps: {
        children: /* @__PURE__ */ jsx(
          DrawerHeader,
          {
            handlers,
            actions,
            warehouseFiltersLegend,
            drawerOptionsDetails,
            announcement,
            formik,
            isFMW,
            isCMW,
            productImageData,
            labels,
            bodyText,
            altBodyText: msStore.key_value.altbody,
            drawerFindBtnLabel,
            options,
            isFiltersOpen,
            state,
            handleFilterCheckbox,
            wioEnabledPricingText,
            analyticsIdentifier
          }
        ),
        closeButtonAriaLabel,
        onClose: handleCloseDrawer,
        title: titleText,
        uniqueId: "warehousedrawer"
      },
      anchor: isFMW ? "left" : "right",
      open: isOpen,
      scrollArea: "body",
      children: /* @__PURE__ */ jsx(
        DrawerBody,
        {
          onUpdatePreferredWarehouse,
          onClose: handleCloseDrawer,
          isLoading,
          warehouses,
          locale,
          moduleSettings,
          preferredWarehouse,
          isFMW,
          isCMW,
          rootRef,
          sentinelRef,
          displayedWarehouses,
          drawerType,
          showSentinel,
          notificationAlert,
          pageName,
          productFlags,
          featureFlags: featureConfigs == null ? void 0 : featureConfigs.custom
        }
      )
    }
  );
};
WarehouseDrawerUI.displayName = COMPONENT_NAME;
export {
  WarehouseDrawerUI
};
